import "./ShareDashboardDialog.less";
import { replace } from "lodash";
import React from "react";
import { axios } from "@/services/axios";
import PropTypes from "prop-types";
import Switch from "antd/lib/switch";
import Modal from "antd/lib/modal";
import Form from "antd/lib/form";
import Alert from "antd/lib/alert";
import notification from "@/services/notification";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import InputWithCopy from "@/components/InputWithCopy";
import HelpTrigger from "@/components/help-trigger/HelpTrigger";
import { useTheme } from "@/components/ThemeProvider";

const API_SHARE_URL = "api/dashboards/{id}/share";

function ShareDashboardDialog({ dialog, dashboard, hasOnlySafeQueries }) {
  const [saving, setSaving] = React.useState(false);
  const { isDarkMode } = useTheme();

  const apiUrl = replace(API_SHARE_URL, "{id}", dashboard.id);
  const enabled = hasOnlySafeQueries || dashboard.publicAccessEnabled;

  const formItemProps = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
    style: { marginBottom: 7 },
  };

  const enableAccess = () => {
    setSaving(true);

    axios
      .post(apiUrl)
      .then(data => {
        dashboard.publicAccessEnabled = true;
        dashboard.public_url = data.public_url;
      })
      .catch(() => {
        notification.error("Failed to turn on sharing for this dashboard");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const disableAccess = () => {
    setSaving(true);

    axios
      .delete(apiUrl)
      .then(() => {
        dashboard.publicAccessEnabled = false;
        delete dashboard.public_url;
      })
      .catch(() => {
        notification.error("Failed to turn off sharing for this dashboard");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const onChange = checked => {
    if (checked) {
      enableAccess();
    } else {
      disableAccess();
    }
  };

  const headerContent = (
    <React.Fragment>
      Share Dashboard
      <div className="modal-header-desc">
        Allow public access to this dashboard with a secret address. <HelpTrigger type="SHARE_DASHBOARD" />
      </div>
    </React.Fragment>
  );

  return (
    <Modal 
      {...dialog.props} 
      title={headerContent} 
      footer={null}
      className={`share-dashboard-dialog ${isDarkMode ? "dark-mode" : ""}`}
    >
      <Form layout="horizontal">
        {!hasOnlySafeQueries && (
          <Form.Item>
            <Alert
              message="For your security, sharing is currently not supported for dashboards containing queries with text parameters. Consider changing the text parameters in your query to a different type."
              type="error"
            />
          </Form.Item>
        )}
        <Form.Item label="Allow public access" {...formItemProps}>
          <Switch
            checked={dashboard.publicAccessEnabled}
            onChange={onChange}
            loading={saving}
            disabled={!enabled}
            data-test="PublicAccessEnabled"
          />
        </Form.Item>
        {dashboard.public_url && (
          <Form.Item label="Secret address" {...formItemProps}>
            <InputWithCopy value={dashboard.public_url} data-test="SecretAddress" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

ShareDashboardDialog.propTypes = {
  dashboard: PropTypes.object.isRequired,
  hasOnlySafeQueries: PropTypes.bool.isRequired,
  dialog: DialogPropType.isRequired,
};

export default wrapDialog(ShareDashboardDialog);
